import axios from "axios";
import { useEffect, useState } from "react";

const KurikulumPdf = () => {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [pdfList, setPdfList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const token = localStorage.getItem("token");
  const API = `${process.env.REACT_APP_API_URL}api/pdf`;

  /* ================= GET DATA ================= */
  const fetchPdf = async () => {
    try {
      const res = await axios.get(API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPdfList(res.data);
    } catch (error) {
      console.error(error);
      alert("Gagal mengambil data PDF");
    }
  };

  useEffect(() => {
    fetchPdf();
  }, []);

  /* ================= HANDLE FILE ================= */
  const handleFileChange = (e) => {
    const selected = e.target.files[0];

    if (!selected) return;

    if (selected.type !== "application/pdf") {
      alert("Hanya file PDF yang diperbolehkan");
      return;
    }

    if (selected.size > 2 * 1024 * 1024) {
      alert("Ukuran file maksimal 2MB");
      return;
    }

    setFile(selected);
  };

  /* ================= UPLOAD ================= */
  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file || !title) {
      alert("Lengkapi semua data");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("file", file);

    try {
      setLoading(true);

      await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Upload berhasil");

      setTitle("");
      setFile(null);
      fetchPdf();
    } catch (error) {
      alert("Gagal upload file");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DOWNLOAD ================= */
  const handleDownload = async (id, title) => {
    try {
      const response = await axios.get(`${API}/${id}/download`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${title}.pdf`;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Gagal download file");
    }
  };

  /* ================= PREVIEW ================= */
  const handlePreview = async (id, title) => {
    try {
      setPreviewLoading(true);

      const response = await axios.get(`${API}/${id}/download`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      setPreviewUrl(url);
      setPreviewTitle(title);
    } catch (error) {
      alert("Gagal menampilkan PDF");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTitle("");
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus file?")) return;

    try {
      await axios.delete(`${API}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      fetchPdf();
    } catch (error) {
      alert("Gagal menghapus file");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-blue-700">
        Upload Dokumen Kurikulum (PDF)
      </h1>

      {/* ================= FORM UPLOAD ================= */}
      <form
        onSubmit={handleUpload}
        className="bg-white shadow-lg rounded-xl p-6 mb-8"
      >
        <div className="grid md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Judul Dokumen"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="border p-2 rounded w-full"
          />

          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="border p-2 rounded w-full"
          />

          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-700"
          >
            {loading ? "Mengupload..." : "Upload"}
          </button>
        </div>

        <p className="text-sm text-gray-500 mt-2">
          Maksimal ukuran file 2MB dan hanya format PDF.
        </p>
      </form>

      {/* ================= TABEL FILE ================= */}
      <div className="bg-white shadow-lg rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">Daftar Dokumen</h2>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse border">
            <thead className="bg-blue-100">
              <tr>
                <th className="border p-2">No</th>
                <th className="border p-2">Judul</th>
                <th className="border p-2">Tanggal Upload</th>
                <th className="border p-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {pdfList.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center p-4">
                    Belum ada dokumen
                  </td>
                </tr>
              ) : (
                pdfList.map((pdf, index) => (
                  <tr key={pdf._id} className="hover:bg-gray-50">
                    <td className="border p-2 text-center">{index + 1}</td>
                    <td className="border p-2">{pdf.title}</td>
                    <td className="border p-2 text-center">
                      {new Date(pdf.createdAt).toLocaleDateString()}
                    </td>
                    <td className="border p-2 text-center space-x-2">
                      <button
                        onClick={() => handlePreview(pdf._id, pdf.title)}
                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                      >
                        Lihat
                      </button>

                      <button
                        onClick={() => handleDownload(pdf._id, pdf.title)}
                        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                      >
                        Download
                      </button>

                      <button
                        onClick={() => handleDelete(pdf._id)}
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                      >
                        Hapus
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MODAL PREVIEW ================= */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-11/12 h-5/6 rounded-xl shadow-lg p-4 relative">
            <h3 className="text-lg font-semibold mb-2">
              Preview: {previewTitle}
            </h3>

            <button
              onClick={closePreview}
              className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded"
            >
              Tutup
            </button>

            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <p>Memuat PDF...</p>
              </div>
            ) : (
              <iframe
                src={previewUrl}
                title="PDF Preview"
                className="w-full h-full border rounded"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KurikulumPdf;
